import {IAgoraRTCRemoteUser, ILocalAudioTrack, UID} from "agora-rtc-sdk-ng";
import React, {
    useCallback, useEffect, useRef, useContext, useLayoutEffect,
} from "react";
import {Container} from "react-bootstrap";
import {getLatestsUndoneEvents} from "../../Components/Api/EventApi";
import {useKeepAliveGenSocket} from "../../hooks/useKeepAlive";
import useXoltarNavigation from "../../hooks/useXoltarNav";
import WebsocketEventer from "../../utils/websockets/WebsocketEventer";
import {registerWSClient} from "../../utils/websockets/websockets";
import ActionButton2, {AnimatedGif, MicActionButton} from "../../Components/ActionButton";
import {getBackendInfo} from "../../Components/Api/ServiceApi";
import {useAppDispatch, useAppSelector} from "../../redux-stores/hooks/hooks";
import {clearEventId, clearUserId, selectConvInfo} from "../../redux-stores/reducers/ConversationState";
import {
    selectIsOneTime,
    selectOneTimeID,
    selectSiteSettings,
} from "../../redux-stores/reducers/PageState";
import {selectQuestionnaireAnswer} from "../../redux-stores/reducers/QuestionerState";
import {startRecording, endRecording} from "../../Components/Api/RecorecdApi";
import {
    selectProlificExp,
    selectProlificUser,
    selectRunProlific,
} from "../../redux-stores/reducers/ProlificExperiments";
import {clearOneTimeLink, markExpDone} from "../../Components/Api/headless";
import {WebSocketContext} from "../../Components/Context/WebSocketContext";

import routesMap from "../../Components/Router/RouterMap";
import {useAgora} from "../../hooks/agorahooks";
import {useUserPing} from "../../hooks/useUserPing";

import "./Conversation.css";
// const phoneBg = require("../../assets/TranspDevice_no_unclaibarte.png");

const onOpenCallback = async (
    eventId?: string,
    userId?: string,
    cam?: string,
    mic?: string,
    wsEventer?: WebsocketEventer | null,
    localViewContainer?: HTMLDivElement | null,
    joinChannel?: (
        amId: string,
        micId: string,
        localView?: HTMLDivElement | null
    ) => Promise<[UID, string]>
) => {
    if (eventId && userId && cam && mic && wsEventer && joinChannel) {
        console.debug("On onOpenCallback");
        try {
            console.log("Joining channel");

            const [uid, channel] = await joinChannel(cam, mic, localViewContainer);

            let recordedPromise: Promise<any> | undefined;
            if (uid) {
                console.log("Start request record " + uid);
                const backend = await getBackendInfo();
                if (backend.webapp) {
                    console.log("Start record " + uid);
                    recordedPromise = startRecording(
                        backend.webapp,
                        `${uid}`,
                        channel,
                        eventId
                    );
                    console.log("Finish request record " + uid);
                }
            }
            const result = await Promise.allSettled([
                registerWSClient(wsEventer, eventId, userId),
                recordedPromise,
            ]);
            result.forEach(
                (res) => res.status === "rejected" && console.error(res.reason)
            );
        } catch (error) {
            console.error(error);
        }
    }
};

const onRemoteCoreFrameArrived = async (
    remoteUser: IAgoraRTCRemoteUser,
    frameDecoded: boolean = false
) => {
    const loadingImage = document.getElementById("img-waiting");
    const buttonsView = document.getElementById("buttons-view");
    console.debug("onRemoteCoreFrameArrived", frameDecoded);
    console.debug("agentArrived", buttonsView?.style.display);
    console.debug("loadingImage", loadingImage?.style.display);

    if (loadingImage) loadingImage.style.display = "none";
    if (buttonsView) buttonsView.className = "";
};

const Conversation = () => {
    useUserPing();
    const dispatch = useAppDispatch();
    const navigation = useXoltarNavigation();
    const conversationInfo = useAppSelector(selectConvInfo);
    const siteSettings = useAppSelector(selectSiteSettings);
    const answer = useAppSelector(selectQuestionnaireAnswer);
    const isRunningProlific = useAppSelector(selectRunProlific);
    const exp = useAppSelector(selectProlificExp);
    const prolificUser = useAppSelector(selectProlificUser);
    const runDemo = useAppSelector(selectIsOneTime);
    const demoId = useAppSelector(selectOneTimeID);
    const mainDiv = useRef<HTMLDivElement>(null);
    const localViewRef = useRef<HTMLDivElement>(null);
    const wsEventer = useContext(WebSocketContext);

    const agora = useAgora(
        conversationInfo.conversation.eventId ?? "",
        undefined,
        async () => await onLeave(),
        onRemoteCoreFrameArrived
    );

    const onLeave = useCallback(async () => {
        let promises: Promise<any>[] = [];
        // TODO: This clearing cache should be flag based
        const eID = conversationInfo.conversation.eventId;
        dispatch(clearUserId());
        dispatch(clearEventId());
        const backend = await getBackendInfo();
        if (backend.webapp && eID) {
            promises.push(endRecording(backend.webapp, eID, eID));
        }
        agora.agoraClient.localTracks.forEach((t) => {
            const labled = t.getTrackLabel();
            console.debug(labled, t.isPlaying);
            console.debug(labled, t.getMediaStreamTrack().readyState);
            t.on("track-ended", () => {
                console.debug(labled, "Ended");
            });
            t.getMediaStreamTrack().stop();
            t.close();

            console.debug(labled, t.isPlaying);
            console.debug(labled, t.getMediaStreamTrack().readyState);
        });
        try {
            await agora.agoraClient.unpublish(agora.agoraClient.localTracks);
        } catch (error) {
            console.warn("Unpublished already done");
        }

        if (wsEventer && conversationInfo.conversation.eventId)
            promises.push(
                wsEventer.unregisterFromChannel(
                    conversationInfo.conversation.eventId,
                    `${agora.agoraClient.uid}`
                )
            );
        try {
            if (
                isRunningProlific &&
                prolificUser &&
                exp &&
                conversationInfo.conversation.eventId
            )
                promises.push(
                    markExpDone(
                        prolificUser.prolificId,
                        exp,
                        conversationInfo.conversation.eventId
                    )
                );
        } catch (error) {
        }
        try {
            if (runDemo && demoId) {
                promises.push(clearOneTimeLink(demoId));
            }
        } catch (error) {
        }
        const results = await Promise.allSettled([
            agora.leaveChannel(),
            ...promises,
        ]);
        results.forEach(
            (res) => res.status === "rejected" && console.error(res.reason)
        );
        if (siteSettings && siteSettings.Feedback) {
            try {
                const {Feedback: feedback} = siteSettings;
                if (feedback.external && feedback.goto) {
                    if (Array.isArray(siteSettings.Feedback.params)) {
                        const myUrl = new URL(siteSettings.Feedback.goto);
                        siteSettings.Feedback.params.forEach((entry) => {
                            if (typeof entry === "string" && answer[entry]) {
                                myUrl.searchParams.append(entry, answer[entry]);
                            }
                        });
                        window.location.href = myUrl.toString();
                        return;
                    }
                }
            } catch (error) {
                console.error(error);
            }
            if (eID) {
                document.location.href =
                    routesMap.Feedback.default +
                    "?" +
                    new URLSearchParams({
                        feedbackId: eID,
                    }).toString();
            } else {
                document.location.href = routesMap.Intro.default;
            }
        } else {
            navigation("Intro");
        }
        // eslint-disable-next-line
    }, [
        agora.leaveChannel,
        agora.joinChannel,
        agora.agoraClient,
        siteSettings,
        answer,
    ]);

    useKeepAliveGenSocket(wsEventer ?? null);
    const onOpen = React.useCallback(async () => {
        console.debug("On open called");
        const eventId = conversationInfo.conversation.eventId;
        const userId = conversationInfo.conversation.userId;
        const cam = conversationInfo.hardware.cam;
        const mic = conversationInfo.hardware.mic;
        const localViewContainer = localViewRef.current;
        return await onOpenCallback(
            eventId,
            userId,
            cam,
            mic,
            wsEventer,
            localViewContainer,
            agora.joinChannel
        );
        // eslint-disable-next-line
    }, [
        conversationInfo.conversation.eventId,
        conversationInfo.conversation.userId,
        conversationInfo.hardware.cam,
        conversationInfo.hardware.mic,
        wsEventer,
        localViewRef,
        localViewRef.current,
        agora.joinChannel,
    ]);
    useLayoutEffect(() => {
        wsEventer?.on("open", onOpen);
        return () => {
        };
        // eslint-disable-next-line
    }, []);
    useEffect(() => {
        const {eventId, userId} = conversationInfo.conversation;

        if (!eventId || !userId) {
            navigation("Intro");
            return;
        }
        getLatestsUndoneEvents(userId).then((response) => {
            if (!response || !response.event || response.status === "DONE") {
                navigation("Intro");
                return;
            }
        });

        return () => {
        };
        // eslint-disable-next-line
    }, []);
    useEffect(() => {
        if (wsEventer) {
            wsEventer.on("message", (msg: any) => {
                console.debug(JSON.stringify(msg));
            });
        }
        // eslint-disable-next-line
    }, [wsEventer]);
    const getStreams = () => {
        return {
            local: agora.agoraClient.localTracks
                .filter((st) => st.trackMediaType === "audio")
                .at(0) as ILocalAudioTrack,
            remote: agora.agoraClient.remoteUsers.find((u) => `${u.uid}` === "5")
                ?.audioTrack,
        };
    };
    return (
        <>
            <div
                style={{
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                    left: 0,
                    top: 0,
                }}
            >
                <Container
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        height: "100%",
                        width: "100%",
                    }}
                >
                    <div
                        id="img-waiting"
                        className="my-auto mx-auto"
                        style={{height: "50vh", display: "flex", flexDirection: "column"}}
                    >
                        <img
                            height={"100%"}
                            alt="Loading"
                            src={require("../../assets/loading_trans.gif")}
                        />
                        <br/>
                        <div>
                            <h4 style={{textAlign: "center"}}>
                                {"Waiting for agent to see you"}
                            </h4>
                        </div>
                    </div>
                </Container>
            </div>
            <div
                ref={mainDiv}
                style={{
                    width: "100%",
                    height: "100%",
                }}
            >
                <div
                    id="local-view"
                    ref={localViewRef}
                    style={{
                        width: "15vw",
                        height: "15vh",
                        position: "absolute",
                        right: 0,
                        zIndex: 2,
                    }}
                ></div>
                <AnimatedGif
                    style={{
                        maxHeight: "6rem",
                        maxWidth: "6rem",
                        position: "absolute",
                        left: 0,
                        zIndex: 2,
                    }}
                    getStreams={getStreams}
                />
                <div
                    style={{
                        display: "flex",
                        width: "100%",
                        height: "100%",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <div id="VideoConteiner" className="VideoConteiner">
                        <div id="buttons-view" className="hide">
                            <ActionButton2
                                variant="camera"
                                style={{maxHeight: "6rem", maxWidth: "6rem"}}
                                onClick={(s) => {
                                    agora.toggleVideo();
                                }}
                            />

                            <MicActionButton
                                style={{maxHeight: "6rem", maxWidth: "6rem"}}
                                getStreams={getStreams}
                                onClick={(s) => {
                                    agora.toggleAudio();
                                }}
                            />
                            <ActionButton2
                                variant="telephone"
                                style={{maxHeight: "6rem", maxWidth: "6rem"}}
                                onClick={() => {
                                    onLeave();
                                }}
                            />
                        </div>
                        {/* <div id="phone-container" className="phone-bg">
              <img id="phone-img" className="phone-bg" src={phoneBg} />
            </div> */}
                    </div>
                </div>
            </div>
        </>
    );
};

export default Conversation;
